import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, List, ListItem, ListItemButton, ListItemAvatar, ListItemText, Avatar, Box, CircularProgress, InputAdornment, IconButton, Button, Typography } from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import axios from 'axios';

const CLOUD_API = 'https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api';

const MovieSearchModal = ({ open, onClose, onSelect }) => {
  const [query, setQuery] = useState('');
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const handleSearch = async (searchQuery, pageNum = 1) => {
    if (!searchQuery.trim()) {
      setMovies([]);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${CLOUD_API}/movies/search/text`, {
        params: { q: searchQuery, page: pageNum, limit: 20 }
      });
      const newMovies = response.data.results || [];
      setMovies(pageNum === 1 ? newMovies : [...movies, ...newMovies]);
      setTotalPages(response.data.total_pages || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Error searching movies:', err);
      setMovies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    handleSearch(query, page + 1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && query.length >= 2) {
      handleSearch(query, 1);
    }
  };

  const handleClear = () => {
    setQuery('');
    setMovies([]);
    setPage(1);
    setTotalPages(0);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Search & Select Movie</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search for a movie (press Enter to search)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{ mb: 2, mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: query && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClear}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {loading && page === 1 ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={30} />
            <Box ml={2}>Searching movies...</Box>
          </Box>
        ) : movies.length > 0 ? (
          <>
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {movies.map((movie) => (
                <ListItem key={movie.id} disablePadding>
                  <ListItemButton onClick={() => onSelect(movie)} sx={{ py: 1.5 }}>
                    <ListItemAvatar>
                      <Avatar 
                        src={movie.poster} 
                        variant="rounded" 
                        sx={{ width: 56, height: 84 }}
                      >
                        {!movie.poster && movie.name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={movie.name}
                      secondary={
                        <>
                          {movie.release_date ? new Date(movie.release_date).getFullYear() : 'N/A'} • {movie.language || 'N/A'}
                          {movie.hero && ` • ${movie.hero}`}
                        </>
                      }
                      sx={{ ml: 2 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {page < totalPages && (
              <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={1}>
                <Button onClick={handleLoadMore} disabled={loading} variant="outlined">
                  {loading ? <CircularProgress size={20} /> : 'Load More'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Page {page} of {totalPages}
                </Typography>
              </Box>
            )}
          </>
        ) : query.length >= 2 ? (
          <Box textAlign="center" py={4} color="text.secondary">
            No movies found. Try a different search term.
          </Box>
        ) : (
          <Box textAlign="center" py={4} color="text.secondary">
            Type at least 2 characters and press Enter to search
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MovieSearchModal;
